import moment from "moment-timezone";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { DatePicker, Form, Switch } from "antd";
import { Drawer } from "../../../../components/drawer/drawer";
import { DrawerHeader } from "../../../../components/drawer/drawer-header";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import {
  FormFieldName,
  FormHeaderTextarea,
  FormSelect,
} from "../../../../components/form/form";
import encodeSvgWithColor from "../../../../common/utils/encodeSvgWithColor";
import ApiTaskManagement from "../../../../api/task-managements";
import useNotification from "../../../../components/notification/notification";
import { Quiz } from "../tasks/task-drawer";
import TaskTypeChip from "../task-type-chip";
import ReminderList from "../../../../components/reminder-time/reminder-list";
import { earliestTimeToday } from "../../../../components/reminder-time/earliest-time-today";
import { Editor } from "../../../../components/editor/editor";
import { useCan } from "../../../../common/utils/usePermissions";
import { useSignedHtml } from "../../../../components/editor/refreshSignedMedia";
import {
  remindersForPersonalTask,
  remindersFromActivity,
  rememberReminderTypeDraft,
  remindersToPayload,
} from "../../../administration/plan-management/components/reminder-mapping";
import ApiProfileManagement from "../../../../api/profile-management";
import ApiSubjectsManager from "../../../../api/subjects-manager";
import { triggerFieldOptions } from "../../../administration/plan-management/components/trigger-field-options";
import "../styles.css";

// The API reports errors either as a string or as { code }; rendering the raw
// value would crash on the object form.
const errorTextOf = (err, fallback) => {
  const value = err?.response?.data?.error;
  if (typeof value === "string" && value) return value;
  if (value && typeof value.code === "string") return value.code;
  return fallback;
};

const DURATION_OPTIONS = Array.from({ length: 99 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}));

// Portal-style drawer used exclusively for admin-assigned SubjectTasks.
// It exposes Edit/Delete affordances (via DrawerHeader's edit icon and
// 3-dots menu), with the same actionable-window guard the backend enforces:
// edit & delete are only offered while status === 'PENDING' and the task
// hasn't crossed its lockAt. Tasks coming from the plan template or self-
// assigned by the subject are shown via the original <TaskDrawer> instead.
const AssignedTaskDrawer = ({
  open,
  onClose,
  task,
  subjectId,
  timezone,
  preview = false,
  onRemoved,
  onUpdated,
}) => {
  const { status, dueDate, completedAt, response } = task || {};
  const { backgroundColor, avatar, avatarColor, title, description } =
    task?.DailyTask || {};
  // its media carries signed URLs that expire, so they are refreshed on open
  const signedDescription = useSignedHtml(description, open);

  const lockedAt = task?.lockAt ? new Date(task.lockAt).getTime() : null;
  const isLocked = !!lockedAt && lockedAt <= Date.now();
  // Edit/delete only for admin-assigned tasks; plan / trigger / self-assigned
  // tasks are preview-only. Future-tasks preview is ALWAYS read-only — the task
  // is computed and has no persisted row to edit or remove. Both need
  // `plan.manage` on the server (LF360-2024).
  const holdsPlanManage = useCan("plan.manage");
  const canManage =
    holdsPlanManage && !preview && !!task?.assignedByUserId && status === "PENDING" && !isLocked && !!subjectId;

  const tz = timezone || task?.plannedTimeZone || "UTC";
  const { openNotification } = useNotification();
  const [form] = Form.useForm();

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duration, setDuration] = useState(1);
  const [rescheduling, setRescheduling] = useState(true);
  // LF360-1830: the task's additional reminders, edited alongside its own.
  const [reminders, setReminders] = useState([]);
  // LF360-1940: whether these reminders are this assignment's own or the task's.
  // False means it follows the task, and a publish of the task rewrites it.
  const [remindersCustomised, setRemindersCustomised] = useState(true);
  // LF360-1830: relative personal tasks take their time from a profile field.
  const [notificationType, setNotificationType] = useState("absolute");
  const [timeProfileFields, setTimeProfileFields] = useState([]);
  // The subject's own resolved values: a time field they have never answered
  // cannot schedule anything, and the backend refuses such an assignment.
  const [subjectCustomFields, setSubjectCustomFields] = useState(null);
  const reminderTypeDrafts = useRef(new Map());

  // Back to the task's own. Saved as this same form with the marker cleared —
  // the backend takes whatever the task carries now and stops treating these as
  // this assignment's choice, so there is no separate call to make. Saving is
  // what applies it, like every other field here.
  const handleResetReminders = () => {
    // Taken exactly as the task has it, a time that has already gone today
    // included: it is the task's answer, and a reminder that cannot be sent
    // today is not a reason to invent one nobody chose (see the assign drawer).
    setRemindersCustomised(false);
    setReminders(remindersForPersonalTask(task?.DailyTask?.defaultReminders));
  };

  // Editing them here is what makes them this assignment's own — the same
  // moment the plan panels mark an activity.
  const handleRemindersChange = (next) => {
    setRemindersCustomised(true);
    const key = task?.id || "assignment";
    rememberReminderTypeDraft(reminderTypeDrafts.current, key, notificationType, next);
    setReminders(next);
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    ApiProfileManagement.getProfiles({ inputType: ["message:time"] })
      .then(({ data }) => {
        if (!cancelled) setTimeProfileFields(data?.subjectProfileFields || []);
      })
      .catch(() => {
        if (!cancelled) setTimeProfileFields([]);
      });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open || !subjectId) return undefined;
    // Guarded: a reply for the previous subject must not decide which trigger
    // fields this one may use.
    let cancelled = false;
    setSubjectCustomFields(null);
    ApiSubjectsManager.getSubject(subjectId)
      .then(({ data }) => {
        if (!cancelled) setSubjectCustomFields(data?.subject?.customFields || []);
      })
      // Left null on failure, so nothing is disabled on incomplete information.
      .catch(() => {
        if (!cancelled) setSubjectCustomFields(null);
      });
    return () => { cancelled = true; };
  }, [open, subjectId]);

  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      form.resetFields();
      setDuration(1);
      setRescheduling(true);
      setReminders([]);
      setNotificationType("absolute");
      reminderTypeDrafts.current.clear();
      // Subject-specific: it must not survive into the next open (LF360-1830).
      setSubjectCustomFields(null);
    }
  }, [open, form]);

  useEffect(() => {
    if (!task) return;
    reminderTypeDrafts.current.clear();
    const plannedDateKey =
      task.plannedDate ||
      (task.plannedAt
        ? moment.tz(task.plannedAt, tz).format("YYYY-MM-DD")
        : null);
    const initialDate = plannedDateKey ? dayjs(plannedDateKey) : null;
    const initialTime =
      Number.isInteger(task.remindHour) && Number.isInteger(task.remindMinute)
        ? dayjs().hour(task.remindHour).minute(task.remindMinute).second(0)
        : null;

    form.setFieldsValue({ date: initialDate, time: initialTime });
    setDuration(Math.max(1, Math.min(99, Number(task.duration) || 1)));
    setRescheduling(task.rescheduling !== false);
    setReminders(remindersFromActivity(task));
    setRemindersCustomised(task.remindersCustomised !== false);
    setNotificationType(task.notificationType === "relative" ? "relative" : "absolute");
  }, [task, tz, form]);

  const todayKey = useMemo(() => moment.tz(tz).format("YYYY-MM-DD"), [tz]);

  // LF360-1940: assigning for today, the backend refuses a reminder that has
  // already passed. The picker is told the same rule so the hours before now are
  // simply not selectable, rather than the admin finding out on Save.
  // Watched rather than read: the constraint has to follow the date the admin
  // picks, and getFieldValue would only ever report what it was on first render.
  // The rule itself is shared with the other drawer and counted in the subject's
  // timezone, which is the one the backend counts in.
  const chosenDate = Form.useWatch("date", form);
  const minMinutes = useMemo(
    () => earliestTimeToday(chosenDate, tz),
    [chosenDate, tz]
  );
  const disabledDate = (current) =>
    current && current.format("YYYY-MM-DD") < todayKey;

  const handleDateChange = (next) => {
    if (next && next.format("YYYY-MM-DD") === todayKey) {
      const time = form.getFieldValue("time");
      if (time) {
        const now = moment.tz(tz);
        const composed = next.hour(time.hour()).minute(time.minute()).second(0);
        if (composed.valueOf() <= now.valueOf()) {
          form.setFieldValue("time", null);
        }
      }
    }
  };

  const handleEdit = () => {
    if (!canManage) return;
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    const plannedDateKey =
      task.plannedDate ||
      (task.plannedAt
        ? moment.tz(task.plannedAt, tz).format("YYYY-MM-DD")
        : null);
    form.setFieldsValue({
      date: plannedDateKey ? dayjs(plannedDateKey) : null,
      time:
        Number.isInteger(task.remindHour) &&
        Number.isInteger(task.remindMinute)
          ? dayjs().hour(task.remindHour).minute(task.remindMinute).second(0)
          : null,
    });
    setDuration(Math.max(1, Math.min(99, Number(task.duration) || 1)));
    setRescheduling(task.rescheduling !== false);
    reminderTypeDrafts.current.clear();
    setReminders(remindersFromActivity(task));
    setRemindersCustomised(task.remindersCustomised !== false);
    setNotificationType(task.notificationType === "relative" ? "relative" : "absolute");
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (_e) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        date: values.date.format("YYYY-MM-DD"),
        duration,
        rescheduling: duration > 1 ? false : rescheduling,
      };
      // The reminder list carries the time. Everything it produces is sent
      // unconditionally: this endpoint is a partial update, where an absent field
      // means "keep", so an emptied list has to say remind:false and a switch back
      // to absolute has to state its type (LF360-1830).
      Object.assign(payload, remindersToPayload(reminders, notificationType));
      // LF360-1940: says whose these are, so a later publish of the task knows
      // whether it may rewrite them.
      payload.remindersCustomised = remindersCustomised;

      const { data } = await ApiTaskManagement.updateAssignedSubjectTask(
        subjectId,
        task.id,
        payload
      );

      openNotification({ type: "success", message: <p>Task updated</p> });
      setIsEditing(false);
      onUpdated && onUpdated(data?.task || null);
    } catch (err) {
      openNotification({
        type: "error",
        message: <p>{errorTextOf(err, "Failed to update task")}</p>,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canManage) return;
    try {
      await ApiTaskManagement.removeAssignedSubjectTask(subjectId, task.id);
      openNotification({ type: "success", message: <p>Task removed</p> });
      onRemoved && onRemoved(task);
    } catch (err) {
      openNotification({
        type: "error",
        message: <p>{errorTextOf(err, "Failed to remove task")}</p>,
      });
    }
  };

  // Icon slot in DrawerHeader mirrors the task-management TaskCard layout:
  // a 72x72 colored tile with the task avatar SVG. The actual title text is
  // rendered below the header as a large read-only FormHeaderTextarea, again
  // matching the task-card pattern so admin-assigned tasks visually belong
  // to the same family as the underlying DailyTask templates.
  const headerIcon = avatar ? (
    <div
      className="icon-wrapper"
      style={{
        width: 72,
        height: 72,
        backgroundColor: backgroundColor || "transparent",
        marginBottom: 10,
      }}
    >
      <img src={encodeSvgWithColor(avatar, avatarColor)} alt="avatar" />
    </div>
  ) : (
    <div />
  );

  return (
    <Drawer open={open} size={680} onClose={onClose} destroyOnHidden>
      <DrawerHeader
        title={headerIcon}
        isEditing={isEditing}
        onlyView={!isEditing && !canManage}
        onEdit={canManage ? handleEdit : undefined}
        onClose={isEditing ? undefined : onClose}
        onCancel={isEditing ? handleCancelEdit : undefined}
        onSave={isEditing ? handleSave : undefined}
        saveLabel="Save"
        isLoading={saving}
        onDelete={canManage ? handleDelete : undefined}
      />

      <div style={{ paddingBottom: 24 }}>
        <FormHeaderTextarea
          key={`title-${task?.id || "none"}`}
          readonly
          defaultValue={title || ""}
          placeholder="Title"
          maxLength={100}
        />
      </div>

      <Form
        form={form}
        layout="horizontal"
        labelCol={{ flex: "160px" }}
        wrapperCol={{ flex: "auto" }}
        labelAlign="left"
        colon={false}
        requiredMark={false}
      >
        {description && (
          <Form.Item>
            <FormFieldName text="Description:" />
            {/* rich text since LF360-1852 — a plain description renders the same */}
            <Editor
              key={`desc-${task?.id || "none"}`}
              readonly
              content={signedDescription}
            />
          </Form.Item>
        )}

        <Form.Item>
          <FormFieldName text="Type:" />
          <div>
            <TaskTypeChip task={task} />
          </div>
        </Form.Item>

        <Form.Item>
          <FormFieldName text="Status:" />
          <div>
            {preview ? (
              <span>Scheduled (preview)</span>
            ) : (
              <UserActivityStatus userStatus={status || "PENDING"} />
            )}
          </div>
        </Form.Item>

        {isEditing ? (
          <>
            <Form.Item
              name="date"
              label={<FormFieldName text="Date:" />}
              rules={[{ required: true, message: "Pick a date" }]}
            >
              <DatePicker
                disabledDate={disabledDate}
                onChange={handleDateChange}
                format="DD MMM YYYY"
                allowClear={false}
                variant="borderless"
                style={{ width: 140 }}
              />
            </Form.Item>

            {/* LF360-1940: the same control the plan panels use. The kind of
                each reminder — a time of day or an offset from a profile answer
                — is chosen inside the picker, so this form no longer asks for a
                notification type or a trigger field of its own.

                No "before assignment" kind: a personally assigned task does not
                exist before its own day, and the backend refuses one. */}
            <Form.Item
              label={(
                <FormFieldName
                  text={remindersCustomised ? "Reminders:" : "Reminders (default):"}
                />
              )}
            >
              <div className="assigned-task-reminders">
                <ReminderList
                  value={reminders}
                  onChange={handleRemindersChange}
                  triggerFields={triggerFieldOptions(timeProfileFields, subjectCustomFields)}
                  minMinutes={minMinutes}
                />

                {/* Under the button that adds one, not beside the list. Only
                    where there is something to go back to: an assignment still
                    following its task has nothing to reset. */}
                {remindersCustomised && (
                  <button
                    type="button"
                    className="assigned-task-reset"
                    onClick={handleResetReminders}
                  >
                    Reset to default
                  </button>
                )}
              </div>
            </Form.Item>

            <Form.Item label={<FormFieldName text="Duration:" />}>
              <FormSelect
                value={duration}
                options={DURATION_OPTIONS}
                onSelect={(value) => {
                  setDuration(value);
                  if (value > 1) setRescheduling(false);
                }}
                style={{ width: 100 }}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </Form.Item>

            <Form.Item label={<FormFieldName text="Reschedule:" />}>
              <Switch
                size="small"
                checked={duration > 1 ? false : rescheduling}
                disabled={duration > 1}
                onChange={setRescheduling}
              />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item>
              <FormFieldName text="Date:" />
              <div>
                {task?.plannedAt
                  ? moment.tz(task.plannedAt, tz).format("DD MMM YYYY")
                  : "—"}
              </div>
            </Form.Item>

            <Form.Item>
              <FormFieldName text="Time:" />
              <div>
                {Number.isInteger(task?.remindHour) &&
                Number.isInteger(task?.remindMinute)
                  ? `${String(task.remindHour).padStart(2, "0")}:${String(
                      task.remindMinute
                    ).padStart(2, "0")}`
                  : "—"}
              </div>
            </Form.Item>

            <Form.Item>
              <FormFieldName text="Duration:" />
              <div>
                {Number.isInteger(task?.duration) ? task.duration : 1}
                {(Number(task?.duration) || 1) === 1 ? " day" : " days"}
              </div>
            </Form.Item>

            <Form.Item>
              <FormFieldName text="Reschedule:" />
              <Switch
                size="small"
                checked={task?.rescheduling !== false}
                disabled
              />
            </Form.Item>

            <Form.Item>
              <FormFieldName text="Due date:" />
              <div>
                {dueDate
                  ? `${moment(dueDate).utc().format("DD MMM YYYY, HH:mm")}, UTC`
                  : "—"}
              </div>
            </Form.Item>

            {completedAt && (
              <Form.Item>
                <FormFieldName text="Completed:" />
                <div>
                  {`${moment(completedAt)
                    .utc()
                    .format("DD MMM YYYY, HH:mm")}, UTC`}
                </div>
              </Form.Item>
            )}
          </>
        )}
      </Form>

      {/* Completion responses — same Quiz block used by the regular TaskDrawer,
          shown when the subject has finished the task so admins see the same
          answer set on assigned tasks as on plan-derived ones. */}
      {!isEditing &&
        completedAt &&
        Array.isArray(response) &&
        response.length > 0 && (
          <div
            style={{
              marginTop: 16,
              border: "1px solid var(--border-color)",
              borderRadius: 8,
              padding: 16,
            }}
          >
            <p style={{ fontWeight: 600, marginBottom: 12 }}>Responses</p>
            <Quiz items={response} />
          </div>
        )}
    </Drawer>
  );
};

export default AssignedTaskDrawer;
